import { useState } from 'react';
import { Link, useLocation, useNavigate, useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { AppLayout } from '../components/templates/AppLayout/AppLayout';
import { Header } from '../components/layout/Header';
import { Sidebar } from '../components/Sidebar';
import { PageContent } from '../components/templates/PageContent/PageContent';
import { PageHeader } from '../components/templates/PageHeader/PageHeader';
import { Section } from '../components/templates/Section/Section';
import { Card } from '../components/atoms/Card/Card';
import { Button } from '../components/atoms/Button/Button';
import { Text } from '../components/atoms/Typography/Text';
import { Badge } from '../components/atoms/Badge/Badge';
import { OverflowMenu } from '../components/atoms/OverflowMenu';
import { DetectedTasks } from '../components/journal/DetectedTasks';
import { formatDay } from '../components/capture/suggestionFormat';
import type { JournalLinkedTask } from '../services/journalService';
import {
  JournalEntryPhotos,
  journalEntryQueryKey,
} from '../components/journal/JournalEntryPhotos';
import { JournalEntryMeasurements } from '../components/journal/JournalEntryMeasurements';
import { getJournalEntry } from '../services/journalService';
import { useTranslation } from '../i18n/useTranslation';
import { formatJournalDate, journalTagLabel } from '../components/journal/journalTags';
import './JournalEntryDetailPage.css';
import { LiveRegion } from '../components/atoms/LiveRegion/LiveRegion';
import { LoadErrorState } from '../components/feedback/LoadErrorState/LoadErrorState';
import { JournalEntryAiAnalysis } from '../components/journalAi/JournalEntryAiAnalysis';
import { JournalEntryCommunitySection } from '../components/community/JournalEntryCommunitySection';

/** Rich-text HTML → plain paragraphs (rendered as text, never as HTML). */
function toParagraphs(html: string | null | undefined): string[] {
  // content is nullable in the API (journal_entries.content) — never call .replace on null.
  return (html ?? '')
    .replace(/<\/(p|div|h[1-6]|li)>|<br\s*\/?>/gi, '\n')
    .replace(/<[^>]*>/g, '')
    .replace(/&nbsp;/g, ' ')
    .replace(/&lt;/g, '<')
    .replace(/&gt;/g, '>')
    .replace(/&quot;/g, '"')
    .replace(/&#39;/g, "'")
    .replace(/&amp;/g, '&')
    .split('\n')
    .map((line) => line.trim())
    .filter((line) => line.length > 0);
}

function LinkedTaskRow({ task, aquariumId, language }: { task: JournalLinkedTask; aquariumId: string; language: string }) {
  const { t } = useTranslation();
  const done = task.status === 'completed';
  const skipped = task.status === 'skipped';
  return (
    <li className="journal-entry-detail-page__linked-task">
      <Badge tone={done ? 'success' : 'neutral'}>
        {done ? t('voice.tasks.statusDone') : skipped ? t('voice.tasks.statusSkipped') : t('voice.tasks.statusOpen')}
      </Badge>
      <Link to={`/aquariums/${encodeURIComponent(aquariumId)}/tasks`} className="journal-entry-detail-page__task-link">
        {task.title}
      </Link>
      {task.dueDate && (
        <Text as="span" variant="caption" color="muted">
          {t('voice.suggestion.dueOn', { date: formatDay(task.dueDate, language) })}
        </Text>
      )}
      {task.isRecurring && (
        <Text as="span" variant="caption" color="muted">{t('voice.tasks.recurring')}</Text>
      )}
    </li>
  );
}

/** One journal entry with its attached photos: /aquariums/:aquariumId/journal/:entryId */
export function JournalEntryDetailPage() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const { aquariumId = '', entryId = '' } = useParams<{ aquariumId: string; entryId: string }>();
  const location = useLocation();
  // Saved with follow-up signal words (UC-B): the form navigates here with { detectTasks: true }.
  const [autoDetect] = useState(() => (location.state as { detectTasks?: boolean } | null)?.detectTasks === true);
  const [detectRequest, setDetectRequest] = useState(0);

  const { data: entry, isLoading, isError, refetch } = useQuery({
    queryKey: journalEntryQueryKey(aquariumId, entryId),
    queryFn: () => getJournalEntry(aquariumId, entryId),
    enabled: Boolean(aquariumId && entryId),
  });

  const back = (
    <Button variant="ghost" onClick={() => navigate(`/aquariums/${aquariumId}/journal`)}>
      {t('journal.detail.back')}
    </Button>
  );

  let body;
  if (isLoading) {
    body = (
      <PageHeader title={t('journal.detail.loadingTitle')} back={back}>
        <LiveRegion data-testid="journal-entry-detail-loading">
          <Text color="muted">{t('journal.detail.loading')}</Text>
        </LiveRegion>
      </PageHeader>
    );
  } else if (isError || !entry) {
    body = (
      <>
        <PageHeader title={t('journal.detail.errorTitle')} back={back} />
        <LoadErrorState
          data-testid="journal-entry-detail-error"
          title={t('journal.detail.loadError')}
          onRetry={() => void refetch()}
        />
      </>
    );
  } else {
    const paragraphs = toParagraphs(entry.content);
    body = (
      <>
        <PageHeader
          title={entry.title}
          description={formatJournalDate(entry.createdAt, i18n.language)}
          back={back}
          actions={
            <OverflowMenu
              label={t('common.moreActions')}
              items={[{ id: 'detect-tasks', label: t('voice.detected.detect'), onSelect: () => setDetectRequest((n) => n + 1) }]}
            />
          }
        />
        <DetectedTasks
          aquariumId={aquariumId}
          entryId={entry.id}
          entryDate={entry.entryDate ?? null}
          autoDetect={autoDetect}
          detectRequest={detectRequest}
        />
        {/* Measurements first: above the entry text and its photos. */}
        <Section title={t('journal.measurements.title')}>
          <Card>
            <JournalEntryMeasurements aquariumId={aquariumId} entryId={entry.id} />
          </Card>
        </Section>
        <Section title={t('journal.detail.contentTitle')}>
          <Card className="journal-entry-detail-page__content" data-testid="journal-entry-detail-content">
            {entry.source === 'voice' && (
              <span className="journal-entry-detail-page__source">
                <Badge tone="info">{t('voice.source.voice')}</Badge>
              </span>
            )}
            {paragraphs.map((paragraph, index) => (
              <Text key={index}>{paragraph}</Text>
            ))}
            {(entry.tags ?? []).length > 0 && (
              <ul className="journal-entry-detail-page__tags" aria-label={t('journal.tags')}>
                {(entry.tags ?? []).map((tag) => (
                  <li key={tag} className="journal-entry-detail-page__tag">
                    {journalTagLabel(tag, t)}
                  </li>
                ))}
              </ul>
            )}
          </Card>
        </Section>
        {(entry.linkedTasks ?? []).length > 0 && (
          <Section title={t('voice.tasks.linkedTitle')}>
            <Card>
              <ul className="journal-entry-detail-page__linked-tasks" data-testid="journal-entry-linked-tasks">
                {(entry.linkedTasks ?? []).map((task) => (
                  <LinkedTaskRow key={task.id} task={task} aquariumId={aquariumId} language={i18n.language} />
                ))}
              </ul>
            </Card>
          </Section>
        )}
        <Section title={t('journal.attachments.title')}>
          <Card>
            <JournalEntryPhotos aquariumId={aquariumId} entryId={entry.id} />
          </Card>
        </Section>
        {/* On-demand AI analysis (audit #7) and, for public entries, likes/comments (audit #11). */}
        <Section title={t('journal.ai.title')}>
          <Card>
            <JournalEntryAiAnalysis aquariumId={aquariumId} entryId={entry.id} />
          </Card>
        </Section>
        <JournalEntryCommunitySection entryId={entry.id} visibility={entry.visibility} />
      </>
    );
  }

  return (
    <AppLayout
      header={<Header />}
      sidebar={<Sidebar />}
      main={
        <PageContent width="default" data-testid="journal-entry-detail-page">
          {body}
        </PageContent>
      }
    />
  );
}

export default JournalEntryDetailPage;
